/**
 * Safely turns any thrown/rejected value into loggable fields.
 *
 * `String(error)` on a non-Error object (e.g. a plain rejected value)
 * yields the useless "[object Object]" and always drops the stack —
 * this is the shared replacement used across server and client logging.
 */

function safeJsonStringify(value: unknown): string {
	try {
		return JSON.stringify(value);
	} catch {
		return Object.prototype.toString.call(value);
	}
}

export interface SerializedError {
	// Index signature so this drops directly into the Record<string, unknown>
	// context param that logger/clientLogger expect, without spreading.
	[key: string]: unknown;
	error_message: string;
	error_name?: string;
	error_stack?: string;
}

export function serializeError(err: unknown): SerializedError {
	if (err instanceof Error) {
		return {
			error_message: err.message,
			error_name: err.name,
			error_stack: err.stack?.substring(0, 4000),
		};
	}

	// Native DOM Events (e.g. a <script onerror>) carry their useful fields
	// (type, target) as prototype getters, not own enumerable properties, so
	// JSON.stringify(event) yields near-nothing (e.g. `{"isTrusted":true}`).
	if (typeof Event !== 'undefined' && err instanceof Event) {
		const target = err.target as { src?: string; tagName?: string } | null;
		const source = target?.tagName ? `<${target.tagName.toLowerCase()}>` : undefined;

		return {
			error_message: [`${err.type} event`, source, target?.src].filter(Boolean).join(' '),
			error_name: 'Event',
		};
	}

	if (typeof err === 'object' && err !== null) {
		return { error_message: safeJsonStringify(err) };
	}

	return { error_message: String(err) };
}
